import { useState } from "react";
import { Loader2, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { field, card } from "@/components/admin/SettingsPanel";
import { AREAS, ROLE_LABEL } from "@/lib/roles";

const EmailForm = ({ user }) => {
  const { setUser } = useAuth();
  const [f, setF] = useState({ new_email: "", current_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", f);
      setUser(data);
      toast.success(`Login email changed. Next time, log in with ${data.email}`);
      setF({ new_email: "", current_password: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className={card} data-testid="change-email-form">
      <h3 className="flex items-center gap-2 font-display text-2xl text-[#F4EEE2]"><Mail className="h-5 w-5 text-[#D4AF37]" /> Change login email</h3>
      <p className="mt-1 text-sm text-[#B7BFCC]">Current: <span data-testid="account-email">{user.email}</span>. Use an inbox you really check, so alerts reach you. You will log in with the new email from now on.</p>
      <div className="mt-5 grid gap-3">
        <input data-testid="account-new-email" type="email" required placeholder="New email (for example you@awainkelet.org)" value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} className={field} />
        <input data-testid="account-email-password" type="password" required placeholder="Your current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={field} />
        <button data-testid="account-save-email-btn" disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3 font-ui text-sm font-semibold text-[#0B111E] disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update email
        </button>
      </div>
    </form>
  );
};

export const AccountPanel = ({ user }) => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const areas = AREAS.filter(([k]) => (user.areas || []).includes(k)).map(([, l]) => l);
  return (
    <div data-testid="account-panel" className="grid gap-4 lg:grid-cols-2">
      <div className={card}>
        <h3 className="font-display text-2xl text-[#F4EEE2]">{user.name}</h3>
        <p className="text-sm text-[#B7BFCC]">{user.email}</p>
        <p className="mt-5 font-ui text-xs uppercase tracking-widest text-[#9AA4B4]">Role</p>
        <p data-testid="account-role" className="mt-1 font-ui text-[#D4AF37]">{ROLE_LABEL[user.role] || user.role}</p>
        <p className="mt-5 font-ui text-xs uppercase tracking-widest text-[#9AA4B4]">Areas you can access</p>
        <p data-testid="account-areas" className="mt-1 text-sm text-[#F4EEE2]">{areas.length ? areas.join(", ") : "None yet — ask your Super Admin."}</p>
      </div>
      <form onSubmit={submit} className={card} data-testid="change-password-form">
        <h3 className="flex items-center gap-2 font-display text-2xl text-[#F4EEE2]"><KeyRound className="h-5 w-5 text-[#D4AF37]" /> Change password</h3>
        <div className="mt-5 grid gap-3">
          <input data-testid="account-current-password" type="password" required placeholder="Current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={field} />
          <input data-testid="account-new-password" type="password" required minLength={8} placeholder="New password (8+ characters)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={field} />
          <input data-testid="account-confirm-password" type="password" required minLength={8} placeholder="Repeat new password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={field} />
          <button data-testid="account-save-password-btn" disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3 font-ui text-sm font-semibold text-[#0B111E] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </button>
        </div>
      </form>
      <EmailForm user={user} />
    </div>
  );
};
